import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import type { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { useContext, useMemo } from "react";
import { SettingsContext } from "../contexts/settings";
import { CustomizationContext } from "./customization-context";
import { resolveCustomizationField } from "./resolve-customization-field";

// Keys in `resourceOverride.COMMON` that the portal-builder confirmation page
// controls. When a merchant edits one of these slots in the builder, we fold
// the resolved value (with three-state semantics) into the settings' COMMON
// overrides so `useReturnConfirmation` and `useSteps` pick it up through the
// existing `copyResource` read path.
const CONFIRMATION_LEGACY_KEY_MAP = {
  heading: "CONFIRMATION_HEADER_TEXT_SUBMIT",
  subtitle: "CONFIRMATION_HEADER_SUBTEXT",
  draftOrderHeading: "DRAFT_ORDER_HEADER",
  draftOrderSubtitle: "DRAFT_ORDER_SUBTITLE",
  stepsTitle: "STATUS_STEPS_TITLE",
  manualReviewTitle: "STATUS_MANUAL_REVIEW_TITLE",
  manualReviewSubtitle: "STATUS_MANUAL_REVIEW_SUBTITLE",
  inTransitTitle: "STATUS_IN_TRANSIT_TITLE",
  inTransitSubtitle: "STATUS_IN_TRANSIT_SUBTITLE",
  processedTitle: "STATUS_PROCESSED_TITLE",
  processedSubtitle: "STATUS_PROCESSED_SUBTITLE",
  processedRepairTitle: "STATUS_PROCESSED_REPAIR_TITLE",
  processedRepairSubtitle: "STATUS_PROCESSED_REPAIR_SUBTITLE",
  dropOffPackageTitle: "DROP_OFF_PACKAGE_TEXT",
  dropOffPackageSubtitle: "DROP_OFF_PACKAGE_SUBTEXT",
} as const;

type ConfirmationSlotKey = keyof typeof CONFIRMATION_LEGACY_KEY_MAP;

/** Pick a slot value out of `customization.pages.confirmation`. */
function pickSlotValue(
  page:
    | NonNullable<NonNullable<PortalCustomization["pages"]>["confirmation"]>
    | null
    | undefined,
  slot: ConfirmationSlotKey,
): string | null | undefined {
  if (!page) {
    return undefined;
  }
  switch (slot) {
    case "heading":
      return page.text?.heading;
    case "subtitle":
      return page.text?.subtitle;
    case "draftOrderHeading":
      return page.draftOrder?.heading;
    case "draftOrderSubtitle":
      return page.draftOrder?.subtitle;
    case "stepsTitle":
      return page.steps?.title;
    case "manualReviewTitle":
      return page.steps?.manualReview?.title;
    case "manualReviewSubtitle":
      return page.steps?.manualReview?.subtitle;
    case "inTransitTitle":
      return page.steps?.inTransit?.title;
    case "inTransitSubtitle":
      return page.steps?.inTransit?.subtitle;
    case "processedTitle":
      return page.steps?.processed?.title;
    case "processedSubtitle":
      return page.steps?.processed?.subtitle;
    case "processedRepairTitle":
      return page.steps?.processedRepair?.title;
    case "processedRepairSubtitle":
      return page.steps?.processedRepair?.subtitle;
    case "dropOffPackageTitle":
      return page.steps?.dropOffPackage?.title;
    case "dropOffPackageSubtitle":
      return page.steps?.dropOffPackage?.subtitle;
  }
}

function resolveConfirmationSettings(
  settings: CustomerPortalAppSettings | undefined,
  customization: PortalCustomization | null,
): CustomerPortalAppSettings | undefined {
  if (!settings) {
    return settings;
  }
  const page = customization?.pages?.confirmation;
  if (!page) {
    return settings;
  }

  // note: legacy is from the old customer portal configuration
  type CommonOverride = NonNullable<
    NonNullable<CustomerPortalAppSettings["resourceOverride"]>["COMMON"]
  >;
  const legacyCommon: CommonOverride = settings.resourceOverride?.COMMON ?? {};
  const mergedCommon: CommonOverride = { ...legacyCommon };

  for (const slot of Object.keys(
    CONFIRMATION_LEGACY_KEY_MAP,
  ) as ConfirmationSlotKey[]) {
    const slotValue = pickSlotValue(page, slot);
    // Empty/missing slots inherit from legacy — don't overwrite it.
    if (!slotValue) {
      continue;
    }
    const legacyKey = CONFIRMATION_LEGACY_KEY_MAP[slot];
    mergedCommon[legacyKey] = resolveCustomizationField(
      slotValue,
      legacyCommon[legacyKey],
    );
  }

  return {
    ...settings,
    resourceOverride: { ...settings.resourceOverride, COMMON: mergedCommon },
  };
}

/** Returns the portal app settings with the portal-builder confirmation-page
 * customizations layered into `resourceOverride.COMMON`. Pass the result to
 * `useReturnConfirmation` so that hook and its nested `useSteps` pick up
 * the merchant's latest edits via their existing `copyResource` read path. */
export function useConfirmationCustomization():
  | CustomerPortalAppSettings
  | undefined {
  const settings = useContext(SettingsContext);
  const { customization } = useContext(CustomizationContext);

  return useMemo(
    () => resolveConfirmationSettings(settings, customization),
    [customization, settings],
  );
}
